import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Filter, Plus, RefreshCw, Search, Server } from 'lucide-react';
import { z } from 'zod';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { Combobox } from '@/components/ui/Combobox';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { SystemName } from '@/components/ui/SystemName';
import { SystemFormDialog } from '@/components/systems/SystemFormDialog';
import type { SystemFormDialogState } from '@/components/systems/SystemFormDialog';
import { useCursorPager } from '@/hooks/useCursorPager';
import { useSystemRoles, useSystems } from '@/hooks/useSystems';
import { useCustomers, useCustomerLocations } from '@/hooks/useCustomers';
import { useServiceTypes } from '@/hooks/useServiceTypes';
import { formatDateTime, policyLabel, scopeLabel } from '@/lib/format';
import type { System, SystemScope, UpdatePolicy } from '@/types/api';

/**
 * Systems-Liste (Sprint 1, WT-103).
 *
 * Filter sind URL-State (deep-linkbar). Filter-Toolbar ist auf Mobile per
 * Toggle einklappbar (Single-Column auf <md).
 *
 * Hinweis zu "eligibility": Das aktuelle OpenAPI-Schema kennt nur
 * `scope ∈ {in_scope, out_of_scope, manual_only}`. Erweiterte Werte
 * (eol/offline/external) werden in Sprint 2/4 nachgezogen (siehe
 * UX-Findings am Ende der Antwort).
 */

// Schema-Werte aus openapi: update_policy ∈ [auto, manual, never, coordinated],
// scope ∈ [in_scope, manual_only, excluded, eol, offline, external].
// role ist seit S8-06 freier Slug-String; Vorschläge liefert GET /systems/roles.
const POLICY_OPTIONS = ['auto', 'manual', 'never', 'coordinated'] as const;
const SCOPE_OPTIONS = [
  'in_scope',
  'manual_only',
  'excluded',
  'eol',
  'offline',
  'external',
] as const;

const searchSchema = z.object({
  q: z.string().optional(),
  customer: z.string().optional(),
  location: z.string().optional(),
  serviceType: z.string().optional(),
  role: z.string().optional(),
  policy: z.enum(POLICY_OPTIONS).optional(),
  scope: z.enum(SCOPE_OPTIONS).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
});

type SearchParams = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/systems/')({
  component: SystemsPage,
  validateSearch: (s) => searchSchema.parse(s),
});

function SystemsPage(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/systems/' });
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [formState, setFormState] = useState<SystemFormDialogState | null>(null);
  const limit = search.limit ?? 50;

  const openCreate = (): void => {
    // Aktiver Kunden-Filter dient als Vorauswahl im Formular.
    setFormState({
      mode: 'create',
      ...(search.customer ? { customerId: search.customer } : {}),
    });
  };

  const customersQuery = useCustomers({ limit: 200 });
  const serviceTypesQuery = useServiceTypes();
  const locationsQuery = useCustomerLocations(search.customer);
  const rolesQuery = useSystemRoles();

  const systemsQuery = useSystems({
    limit,
    ...(search.q ? { search: search.q } : {}),
    ...(search.role ? { role: search.role } : {}),
    ...(search.policy ? { updatePolicy: search.policy } : {}),
    ...(search.scope ? { scope: search.scope } : {}),
    ...(search.customer ? { customerId: search.customer } : {}),
    ...(search.location ? { locationId: search.location } : {}),
    ...(search.serviceType ? { serviceType: search.serviceType } : {}),
    ...(search.cursor ? { cursor: search.cursor } : {}),
  });

  const customerOptions = useMemo(
    () =>
      (customersQuery.data?.data ?? []).map((c) => ({
        value: c.id,
        label: c.name,
        hint: c.slug,
      })),
    [customersQuery.data]
  );

  const locationOptions = useMemo(
    () =>
      (locationsQuery.data?.data ?? []).map((l) => ({
        value: l.id,
        label: l.name,
        ...(l.address ? { hint: l.address } : {}),
      })),
    [locationsQuery.data]
  );

  const serviceTypeOptions = useMemo(
    () =>
      (serviceTypesQuery.data ?? []).map((t) => ({
        value: t.slug,
        label: t.name,
        hint: t.family,
      })),
    [serviceTypesQuery.data]
  );

  const roleOptions = useMemo(() => {
    const opts = (rolesQuery.data?.data ?? []).map((r) => ({
      value: r.role,
      label: r.role,
      ...(r.count > 0 ? { hint: `${r.count} Systeme` } : {}),
    }));
    // Deep-Link mit (noch) unbekannter Rolle: aktiven Filterwert anzeigbar halten.
    if (search.role && !opts.some((o) => o.value === search.role)) {
      opts.unshift({ value: search.role, label: search.role });
    }
    return opts;
  }, [rolesQuery.data, search.role]);

  const onSearch = (event: React.FormEvent): void => {
    event.preventDefault();
    void navigate({
      search: () => ({
        ...stripCursor(search),
        ...(searchInput ? { q: searchInput } : {}),
      }),
    });
  };

  const setFilter = (patch: Partial<SearchParams>): void => {
    void navigate({
      search: () => ({ ...stripCursor(search), ...patch }),
    });
  };

  const total = systemsQuery.data?.meta?.total_estimate ?? null;
  const activeFilterCount = countActiveFilters(search);

  const server = useCursorPager({
    cursor: search.cursor,
    nextCursor: systemsQuery.data?.links?.next ?? null,
    onCursorChange: (cursor) =>
      void navigate({
        search: () => (cursor ? { ...search, cursor } : stripCursor(search)),
      }),
    pageSize: limit,
    onPageSizeChange: (size) =>
      void navigate({ search: () => ({ ...stripCursor(search), limit: size }) }),
    ...(total != null ? { totalLabel: `~${total} Einträge gesamt` } : {}),
  });

  const columns = useMemo<ReadonlyArray<Column<System>>>(
    () => [
      {
        id: 'hostname',
        header: 'Hostname',
        filterValue: (sys) => `${sys.hostname} ${sys.primary_ip ?? ''}`,
        cell: (sys) => {
          const out = sys.scope !== 'in_scope';
          const tooltip = out
            ? `Nicht im Wartungs-Scope: ${scopeLabel(sys.scope)}`
            : undefined;
          return (
            <>
              <SystemName system={sys} asLink {...(tooltip ? { title: tooltip } : {})} />
              {sys.primary_ip && (
                <span className="ml-2 font-mono text-xs text-muted">{sys.primary_ip}</span>
              )}
            </>
          );
        },
      },
      {
        id: 'kunde',
        header: 'Kunde',
        filterValue: (sys) =>
          customerOptions.find((c) => c.value === sys.customer_id)?.label ?? '',
        cell: (sys) => {
          const customerName = customerOptions.find((c) => c.value === sys.customer_id)?.label;
          return customerName ? (
            <Link
              to="/customers/$id"
              params={{ id: sys.customer_id }}
              className="text-primary hover:underline"
            >
              {customerName}
            </Link>
          ) : (
            <span className="font-mono text-xs text-muted">{sys.customer_id.slice(0, 8)}…</span>
          );
        },
      },
      {
        id: 'os',
        header: 'OS',
        filter: 'select',
        filterValue: (sys) => sys.os_family ?? '',
        className: 'text-muted',
        cell: (sys) => sys.os_family ?? '–',
      },
      {
        id: 'backup',
        header: 'Backup',
        filter: 'select',
        filterValue: (sys) => (sys.backup_enabled ? 'ja' : 'nein'),
        cell: (sys) =>
          sys.backup_enabled ? (
            <Badge tone="success">ja</Badge>
          ) : (
            <Badge tone="neutral">nein</Badge>
          ),
      },
      {
        id: 'update',
        header: 'Update',
        filter: 'select',
        filterValue: (sys) => policyLabel(sys.update_policy),
        cell: (sys) => (
          <Badge tone={updatePolicyTone(sys.update_policy)}>
            {policyLabel(sys.update_policy)}
          </Badge>
        ),
      },
      {
        id: 'scope',
        header: 'Scope',
        filter: 'select',
        filterValue: (sys) => scopeLabel(sys.scope),
        cell: (sys) => <Badge tone={scopeTone(sys.scope)}>{scopeLabel(sys.scope)}</Badge>,
      },
      {
        id: 'letzte-wartung',
        header: 'Letzte Wartung',
        filterValue: (sys) =>
          sys.last_maintained_at ? formatDateTime(sys.last_maintained_at) : '',
        className: 'text-muted',
        cell: (sys) => formatDateTime(sys.last_maintained_at),
      },
    ],
    [customerOptions]
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Systeme</h1>
          <p className="text-muted">Wartbare Systeme aller Kunden.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => void systemsQuery.refetch()}
            leftIcon={<RefreshCw className="size-4" />}
          >
            Aktualisieren
          </Button>
          <Button
            leftIcon={<Plus className="size-4" />}
            onClick={openCreate}
            data-testid="system-create"
          >
            Neues System
          </Button>
        </div>
      </header>

      <SystemFormDialog
        state={formState}
        onClose={() => setFormState(null)}
        onSuccess={(system) =>
          void navigate({ to: '/systems/$id', params: { id: system.id } })
        }
      />

      <Card>
        <form onSubmit={onSearch} className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px] flex-1">
              <Input
                label="Suche"
                placeholder="Hostname, IP, Notizen ..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                leftIcon={<Search className="size-4" />}
              />
            </div>
            <Button type="submit">Suchen</Button>
            <Button
              type="button"
              variant="secondary"
              leftIcon={<Filter className="size-4" />}
              aria-expanded={filtersOpen}
              aria-controls="systems-filter-toolbar"
              onClick={() => setFiltersOpen((o) => !o)}
              className="md:hidden"
            >
              Filter {activeFilterCount > 0 && <Badge tone="primary" className="ml-2">{activeFilterCount}</Badge>}
            </Button>
            {activeFilterCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setSearchInput('');
                  void navigate({ search: () => ({}) });
                }}
              >
                Zurücksetzen
              </Button>
            )}
          </div>

          <div
            id="systems-filter-toolbar"
            className={[
              'grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-6',
              filtersOpen ? 'grid' : 'hidden md:grid',
            ].join(' ')}
          >
            <Combobox
              label="Kunde"
              value={search.customer ?? null}
              onChange={(v) =>
                setFilter({
                  ...(v ? { customer: v } : {}),
                  // Standort an Kunde gebunden – bei Wechsel resetten
                  location: undefined,
                })
              }
              options={customerOptions}
              placeholder="Alle Kunden"
            />
            <Combobox
              label="Standort"
              value={search.location ?? null}
              onChange={(v) => setFilter({ ...(v ? { location: v } : { location: undefined }) })}
              options={locationOptions}
              disabled={!search.customer || locationOptions.length === 0}
              placeholder={search.customer ? 'Alle Standorte' : 'Erst Kunde wählen'}
            />
            <Combobox
              label="Service-Typ"
              value={search.serviceType ?? null}
              onChange={(v) => setFilter({ ...(v ? { serviceType: v } : { serviceType: undefined }) })}
              options={serviceTypeOptions}
              placeholder="Alle Stacks"
            />
            <Combobox
              label="Rolle"
              value={search.role ?? null}
              onChange={(v) => setFilter({ ...(v ? { role: v } : { role: undefined }) })}
              options={roleOptions}
              placeholder="Alle Rollen"
            />
            <Combobox
              label="Update-Policy"
              value={search.policy ?? null}
              onChange={(v) =>
                setFilter({
                  ...(v
                    ? { policy: v as UpdatePolicy }
                    : { policy: undefined }),
                })
              }
              options={POLICY_OPTIONS.map((p) => ({ value: p, label: policyLabel(p) }))}
              placeholder="Alle"
            />
            <Combobox
              label="Scope"
              value={search.scope ?? null}
              onChange={(v) =>
                setFilter({
                  ...(v
                    ? { scope: v as SystemScope }
                    : { scope: undefined }),
                })
              }
              options={SCOPE_OPTIONS.map((s) => ({ value: s, label: scopeLabel(s) }))}
              placeholder="Alle"
            />
          </div>
        </form>
      </Card>

      <Card>
        {systemsQuery.isLoading && <SkeletonRows rows={8} cols={7} />}
        {systemsQuery.isError && <ErrorAlert error={systemsQuery.error} />}

        {systemsQuery.data && systemsQuery.data.data.length === 0 && (
          <EmptyState
            icon={<Server className="size-8" />}
            title="Keine Systeme gefunden"
            description={
              activeFilterCount > 0
                ? 'Filter zurücksetzen oder anderen Kunden wählen.'
                : 'Wartungstabelle (.ods) importieren oder ersten Eintrag manuell anlegen.'
            }
            actions={
              activeFilterCount > 0 ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSearchInput('');
                    void navigate({ search: () => ({}) });
                  }}
                >
                  Filter zurücksetzen
                </Button>
              ) : (
                <>
                  <Button leftIcon={<Plus className="size-4" />} onClick={openCreate}>
                    Neues System
                  </Button>
                  <Link to="/settings/import">
                    <Button variant="secondary">ODS importieren</Button>
                  </Link>
                </>
              )
            }
          />
        )}

        {systemsQuery.data && systemsQuery.data.data.length > 0 && (
          <DataTable
            caption="Systeme"
            columns={columns}
            rows={systemsQuery.data.data}
            rowKey={(sys) => sys.id}
            storageKey="systems"
            server={server}
            rowMuted={(sys) => sys.scope !== 'in_scope'}
          />
        )}
      </Card>
    </div>
  );
}

// --- Helper -------------------------------------------------------------

function updatePolicyTone(
  p: string | null | undefined
): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (p) {
    case 'auto':
      return 'success';
    case 'manual':
    case 'coordinated':
      return 'warning';
    case 'never':
      return 'danger';
    default:
      return 'neutral';
  }
}

function scopeTone(s: SystemScope | null | undefined): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (s) {
    case 'in_scope':
      return 'success';
    case 'manual_only':
    case 'external':
      return 'warning';
    case 'excluded':
    case 'eol':
    case 'offline':
      return 'danger';
    default:
      return 'neutral';
  }
}

function stripCursor(s: SearchParams): SearchParams {
  const { cursor: _c, ...rest } = s;
  return rest;
}

function countActiveFilters(s: SearchParams): number {
  return [s.customer, s.location, s.serviceType, s.role, s.policy, s.scope, s.q].filter(
    Boolean
  ).length;
}
